import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes, type ReactNode, type HTMLAttributes } from "react";
import { Link } from "react-router-dom";

/* =====================================================================
   Container — page-width wrapper. Used in every section.
   ===================================================================== */
export function Container({
  className = "",
  children,
}: { className?: string; children: ReactNode }) {
  return (
    <div className={`mx-auto w-full max-w-container px-6 md:px-8 ${className}`}>
      {children}
    </div>
  );
}

/* =====================================================================
   Section — vertical rhythm wrapper.
   ===================================================================== */
export function Section({
  className = "",
  tone = "default",
  children,
  id,
}: {
  className?: string;
  tone?: "default" | "soft" | "forest";
  children: ReactNode;
  id?: string;
}) {
  const toneClasses =
    tone === "soft"
      ? "bg-forest-soft"
      : tone === "forest"
      ? "bg-forest-gradient text-white"
      : "bg-canvas";
  return (
    <section
      id={id}
      className={`${toneClasses} py-16 md:py-24 ${className}`}
    >
      {children}
    </section>
  );
}

/* =====================================================================
   Eyebrow — small uppercase tagline above headings.
   ===================================================================== */
export function Eyebrow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p className={`tagline ${className}`}>{children}</p>
  );
}

/* =====================================================================
   Heading — typed display tiers.
   ===================================================================== */
export function Heading({
  level = 2,
  children,
  className = "",
  id,
}: {
  level?: 1 | 2 | 3 | 4;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  const Tag = (`h${level}`) as "h1" | "h2" | "h3" | "h4";
  const size =
    level === 1
      ? "text-4xl md:text-5xl lg:text-6xl tracking-tightest"
      : level === 2
      ? "text-3xl md:text-4xl"
      : level === 3
      ? "text-xl md:text-2xl"
      : "text-lg md:text-xl";
  return (
    <Tag id={id} className={`${size} font-bold ${className}`}>
      {children}
    </Tag>
  );
}

/* =====================================================================
   Lede — large intro paragraph.
   ===================================================================== */
export function Lede({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p className={`max-w-prose text-lg md:text-xl text-ink-700 leading-relaxed ${className}`}>
      {children}
    </p>
  );
}

/* =====================================================================
   Button — primary / secondary / ghost. Renders <a>, <Link>, or <button>.
   ===================================================================== */
type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

const baseBtn =
  "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors duration-200 ease-material disabled:opacity-50 disabled:pointer-events-none";

const variants: Record<Variant, string> = {
  primary:
    "bg-forest-700 text-white hover:bg-forest-900 shadow-card hover:shadow-cardHover",
  secondary:
    "bg-surface text-ink-900 border border-border hover:border-forest-700 hover:text-forest-700",
  ghost:
    "bg-transparent text-ink-700 hover:text-forest-700",
};

const sizes: Record<Size, string> = {
  sm: "px-3.5 py-2 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", className = "", children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      className={`${baseBtn} ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

type LinkBtnProps = (AnchorHTMLAttributes<HTMLAnchorElement> & {
  to?: string;
  href?: string;
  variant?: Variant;
  size?: Size;
});
export function LinkButton({
  to,
  href,
  variant = "primary",
  size = "md",
  className = "",
  children,
  ...rest
}: LinkBtnProps) {
  const cls = `${baseBtn} ${variants[variant]} ${sizes[size]} ${className}`;
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} {...rest}>
      {children}
    </a>
  );
}

/* =====================================================================
   Card — Material 3 elevated container.
   ===================================================================== */
type CardProps = HTMLAttributes<HTMLElement> & { as?: "div" | "article" | "li" | "section" };
export function Card({
  children,
  className = "",
  as = "div",
  ...rest
}: CardProps) {
  const cls = `rounded-2xl bg-surface border border-border p-6 md:p-8 shadow-card hover:shadow-cardHover transition-shadow duration-200 ease-material ${className}`;
  if (as === "article") return <article className={cls} {...rest}>{children}</article>;
  if (as === "li") return <li className={cls} {...rest}>{children}</li>;
  if (as === "section") return <section className={cls} {...rest}>{children}</section>;
  return <div className={cls} {...rest}>{children}</div>;
}

/* =====================================================================
   Badge — small status pill.
   ===================================================================== */
export function Badge({ children, tone = "forest", className = "" }: {
  children: ReactNode;
  tone?: "forest" | "neutral";
  className?: string;
}) {
  const t =
    tone === "forest"
      ? "bg-forest-50 text-forest-800 border border-forest-100"
      : "bg-ink-100 text-ink-700 border border-ink-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${t} ${className}`}
    >
      {children}
    </span>
  );
}

/* =====================================================================
   Stat — number + label.
   ===================================================================== */
export function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-extrabold text-forest-700 tracking-tightest">
        {value}
      </div>
      <div className="mt-1 text-sm text-ink-600 uppercase tracking-tagline font-semibold">
        {label}
      </div>
    </div>
  );
}

/* =====================================================================
   FeatureCard — icon + headline + body.
   ===================================================================== */
export function FeatureCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card as="article" className="h-full">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
        {icon}
      </div>
      <Heading level={3} className="mt-5">
        {title}
      </Heading>
      <p className="mt-3 text-ink-600">{children}</p>
    </Card>
  );
}

/* =====================================================================
   ExternalLink — semantic external w/ proper rel and indicator.
   ===================================================================== */
export function ExternalLink({
  href,
  children,
  className = "",
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-forest-700 underline underline-offset-2 hover:text-forest-900 ${className}`}
      {...rest}
    >
      {children}
    </a>
  );
}
